import { useState } from 'react';
import { useAdminInvites } from '../../hooks/useAdminInvites';

const ROLES = [
  { value: 'viewer',           label: 'Viewer',           color: '#6b7280', desc: 'Только чтение' },
  { value: 'network_engineer', label: 'Network Engineer', color: '#3b82f6', desc: 'Просмотр + расчёты' },
  { value: 'dc_manager',       label: 'DC Manager',       color: '#8b5cf6', desc: 'Редактирование' },
  { value: 'admin',            label: 'Admin',            color: '#f59e0b', desc: 'Полный доступ' },
];

const TTL_OPTIONS = [
  { label: '1 час',     hours: 1 },
  { label: '24 часа',   hours: 24 },
  { label: '7 дней',    hours: 168 },
  { label: '30 дней',   hours: 720 },
  { label: 'Бессрочно', hours: null },
];

function formatDate(iso?: string) {
  if (!iso) return '—';
  const d = new Date(iso);
  const now = new Date();
  const diff = d.getTime() - now.getTime();
  if (diff < 0) return <span style={{ color: '#ef4444' }}>истёк</span>;
  const h = Math.floor(diff / 3600000);
  const days = Math.floor(h / 24);
  if (days > 0) return `через ${days}д`;
  if (h > 0) return `через ${h}ч`;
  return `через <1ч`;
}

function roleBadge(role: string) {
  const r = ROLES.find(x => x.value === role);
  return (
    <span style={{
      display: 'inline-block',
      padding: '2px 8px', borderRadius: 4,
      fontSize: 11, fontWeight: 600,
      background: (r?.color ?? '#6b7280') + '22',
      color: r?.color ?? '#6b7280',
      border: `1px solid ${(r?.color ?? '#6b7280')}44`,
    }}>
      {r?.label ?? role}
    </span>
  );
}

export function AdminInvitesTab() {
  const {
    invites,
    loading,
    error,
    creating,
    createdCode,
    copied,
    createInvite,
    deleteInvite,
    copyCode,
    refresh
  } = useAdminInvites();

  const [newRole, setNewRole] = useState('network_engineer');
  const [newTtl, setNewTtl] = useState<number | null>(168);
  const [newMaxUses, setNewMaxUses] = useState<number>(1);

  const handleCreate = () => {
    createInvite(newRole, newTtl, newMaxUses);
  };

  return (
    <div>
      {error && (
        <div style={{
          background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)',
          borderRadius: 6, padding: '10px 14px', color: '#f87171', fontSize: 13, marginBottom: 16,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between'
        }}>
          <span>{error}</span>
        </div>
      )}
      
      <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start' }}>
        {/* Left: Create form */}
        <div style={{
          width: 280, flexShrink: 0,
          background: '#111827', border: '1px solid #1f2937',
          borderRadius: 8, padding: 16,
        }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 14 }}>
            Создать инвайт
          </div>

          <div style={{ marginBottom: 12 }}>
            <span className="block text-[11px] text-[#9ca3af] mb-1">Роль</span>
            {ROLES.map(r => (
              <label key={r.value} htmlFor={`role-${r.value}`} style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '6px 8px', borderRadius: 5, marginBottom: 3, cursor: 'pointer',
                background: newRole === r.value ? r.color + '15' : 'transparent',
                border: newRole === r.value ? `1px solid ${r.color}44` : '1px solid transparent',
              }}>
                <input type="radio" id={`role-${r.value}`} name="role" value={r.value}
                  checked={newRole === r.value}
                  onChange={() => setNewRole(r.value)}
                  style={{ accentColor: r.color }} />
                <span>
                  <span style={{ fontSize: 12, color: newRole === r.value ? r.color : '#e5e7eb', fontWeight: 500 }}>{r.label}</span>
                  <span style={{ fontSize: 10, color: '#6b7280', display: 'block' }}>{r.desc}</span>
                </span>
              </label>
            ))}
          </div>

          <div style={{ marginBottom: 14 }}>
            <label htmlFor="ttl-select" style={{ fontSize: 11, color: '#9ca3af', display: 'block', marginBottom: 5 }}>Срок действия</label>
            <select
              id="ttl-select"
              value={newTtl ?? ''}
              onChange={e => setNewTtl(e.target.value ? parseInt(e.target.value) : null)}
              style={{
                width: '100%', background: '#0f172a', border: '1px solid #374151',
                color: '#e5e7eb', borderRadius: 5, padding: '6px 8px', fontSize: 12,
              }}>
              {TTL_OPTIONS.map(o => (
                <option key={o.label} value={o.hours ?? ''}>{o.label}</option>
              ))}
            </select>
          </div>

          <div style={{ marginBottom: 14 }}>
            <label htmlFor="uses-input" style={{ fontSize: 11, color: '#9ca3af', display: 'block', marginBottom: 5 }}>Кол-во использований</label>
            <input
              id="uses-input"
              type="number"
              min="1"
              max="1000"
              value={newMaxUses}
              onChange={e => setNewMaxUses(parseInt(e.target.value) || 1)}
              style={{
                width: '100%', background: '#0f172a', border: '1px solid #374151',
                color: '#e5e7eb', borderRadius: 5, padding: '6px 8px', fontSize: 12,
              }}
            />
          </div>

          <button onClick={handleCreate} disabled={creating}
            style={{
              width: '100%', padding: '8px', fontSize: 13, fontWeight: 600,
              background: creating ? '#1f2937' : 'rgba(59,130,246,0.15)',
              border: `1px solid ${creating ? '#374151' : 'rgba(59,130,246,0.4)'}`,
              borderRadius: 6, color: creating ? '#4b5563' : '#60a5fa',
              cursor: creating ? 'not-allowed' : 'pointer',
            }}>
            {creating ? 'Создание...' : '+ Создать инвайт-код'}
          </button>

          {/* Show created code */}
          {createdCode && (
            <div style={{
              marginTop: 12, padding: '10px 12px',
              background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.25)',
              borderRadius: 6,
            }}>
              <div style={{ fontSize: 10, color: '#6b7280', marginBottom: 6 }}>
                Код создан — скопируйте сейчас, показывается один раз:
              </div>
              <div style={{
                fontFamily: 'JetBrains Mono, monospace', fontSize: 13,
                color: '#34d399', letterSpacing: '0.05em',
                marginBottom: 8, wordBreak: 'break-all',
              }}>
                {createdCode}
              </div>
              <button onClick={() => copyCode(createdCode)}
                style={{
                  width: '100%', padding: '5px', fontSize: 12,
                  background: copied ? 'rgba(16,185,129,0.2)' : 'rgba(16,185,129,0.1)',
                  border: '1px solid rgba(16,185,129,0.3)',
                  borderRadius: 4, color: '#34d399', cursor: 'pointer',
                }}>
                {copied ? '✓ Скопировано' : '⎘ Копировать'}
              </button>
            </div>
          )}
        </div>

        {/* Right: Invites table */}
        <div style={{ flex: 1 }}>
          <div style={{
            background: '#111827', border: '1px solid #1f2937', borderRadius: 8, overflow: 'hidden',
          }}>
            <div style={{
              padding: '12px 16px', borderBottom: '1px solid #1f2937',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: '#e5e7eb' }}>
                Инвайт-коды
                <span style={{ marginLeft: 8, fontSize: 11, color: '#6b7280', fontWeight: 400 }}>
                  ({invites.length} всего · {invites.filter(i => i.is_active).length} активных)
                </span>
              </span>
              <button onClick={refresh} disabled={loading}
                style={{
                  padding: '4px 10px', fontSize: 11, background: 'transparent',
                  border: '1px solid #374151', borderRadius: 5,
                  color: '#9ca3af', cursor: 'pointer',
                }}>
                {loading ? '...' : '↻ Обновить'}
              </button>
            </div>

            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ background: '#0f172a' }}>
                  {['ID', 'Hash', 'Роль', 'Статус', 'Использования', 'Истекает', ''].map(h => (
                    <th key={h} style={{
                      padding: '9px 12px', textAlign: 'left',
                      fontSize: 10, fontWeight: 700, color: '#6b7280',
                      textTransform: 'uppercase', letterSpacing: '0.07em',
                    }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading && invites.length === 0 ? (
                  <tr><td colSpan={7} style={{ padding: '24px', textAlign: 'center', color: '#4b5563' }}>Загрузка...</td></tr>
                ) : invites.length === 0 ? (
                  <tr><td colSpan={7} style={{ padding: '24px', textAlign: 'center', color: '#4b5563' }}>Нет инвайт-кодов</td></tr>
                ) : invites.map(inv => (
                  <tr key={inv.id} style={{
                    borderBottom: '1px solid #1f2937',
                    background: !inv.is_active ? 'rgba(0,0,0,0.2)' : 'transparent',
                  }}>
                    <td style={{ padding: '9px 12px', color: '#6b7280', fontFamily: 'monospace' }}>#{inv.id}</td>
                    <td style={{ padding: '9px 12px', fontFamily: 'monospace', color: '#9ca3af', fontSize: 11 }}>
                      {inv.code_hash.slice(0, 12)}…
                    </td>
                    <td style={{ padding: '9px 12px' }}>{roleBadge(inv.role)}</td>
                    <td style={{ padding: '9px 12px' }}>
                      {inv.is_active
                        ? <span style={{ color: '#34d399', fontSize: 11, fontWeight: 600 }}>● активен</span>
                        : <span style={{ color: '#6b7280', fontSize: 11 }}>✓ истёк/исчерпан</span>
                      }
                    </td>
                    <td style={{ padding: '9px 12px', color: '#e5e7eb' }}>
                      {inv.used_count} / {inv.max_uses}
                    </td>
                    <td style={{ padding: '9px 12px', color: '#9ca3af' }}>
                      {inv.expires_at ? formatDate(inv.expires_at) : <span style={{ color: '#4b5563' }}>∞</span>}
                    </td>
                    <td style={{ padding: '9px 12px', textAlign: 'right' }}>
                      <button onClick={() => deleteInvite(inv.id)}
                        style={{
                          padding: '3px 8px', fontSize: 11,
                          background: 'transparent',
                          border: '1px solid rgba(239,68,68,0.3)',
                          borderRadius: 4, color: '#f87171',
                          cursor: 'pointer',
                        }}>
                        Удалить
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Role legend */}
          <div style={{ marginTop: 12, display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            {ROLES.map(r => (
              <span key={r.value} style={{ fontSize: 11, color: '#6b7280', display: 'flex', alignItems: 'center', gap: 5 }}>
                <span style={{ width: 8, height: 8, borderRadius: 2, background: r.color, display: 'inline-block' }} />
                <strong style={{ color: r.color }}>{r.label}</strong> — {r.desc}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
